import DSBox from "views/system-design/DSBox";
import DSChip from "views/system-design/DSChip";
import DSAvatar from "views/system-design/DSAvatar";
import DSTooltip from "views/system-design/DSTooltip";
import DSTypography from "views/system-design/DSTypography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Author } from "api/author";
import { getInitials, getAvatarColor } from "../../utils";
import {
  POLICY_LABEL,
  PULL_STATE_LABEL,
  REPO_LABEL,
  REVIEW_STATUS_COLOR,
  REVIEW_STATUS_LABEL,
  REVIEWER_STATUS_COLOR,
  REVIEWER_STATUS_LABEL,
  ReviewPull,
  ReviewRequest,
  Reviewer,
} from "../types";
import { approvedCount, isTeamRequest, userName } from "../utils";

export function ReviewStatusChip({ request }: { request: ReviewRequest }) {
  const label =
    request.status === "closed" && request.outcome
      ? request.outcome === "merged"
        ? "Merged"
        : "Cancelled"
      : REVIEW_STATUS_LABEL[request.status];
  return (
    <DSChip
      label={label}
      size="small"
      sx={{
        bgcolor: REVIEW_STATUS_COLOR[request.status],
        color: "common.white",
        fontWeight: 500,
        fontSize: 11,
      }}
    />
  );
}

export function PriorityChip({ request }: { request: ReviewRequest }) {
  if (request.priority !== "urgent") return null;
  return (
    <DSChip
      label="Urgent"
      size="small"
      sx={{
        bgcolor: "error.main",
        color: "common.white",
        fontWeight: 600,
        fontSize: 11,
      }}
    />
  );
}

export function PullChip({ pull }: { pull: ReviewPull }) {
  const dimmed = pull.state !== "open";
  return (
    <DSTooltip
      title={`${pull.githubRepo} #${pull.number} · ${PULL_STATE_LABEL[pull.state]}${
        pull.hasNewCommitsSinceRound ? " · new commits since this round" : ""
      }`}
    >
      <DSChip
        clickable
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          window.open(pull.url, "_blank", "noopener,noreferrer");
        }}
        size="small"
        variant="outlined"
        icon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
        label={`${REPO_LABEL[pull.repo]} #${pull.number}`}
        sx={{
          fontSize: 11,
          opacity: dimmed ? 0.6 : 1,
          textDecoration: pull.state === "merged" ? "line-through" : "none",
          borderColor: pull.hasNewCommitsSinceRound
            ? "warning.main"
            : undefined,
        }}
      />
    </DSTooltip>
  );
}

export function PullChips({ pulls }: { pulls: ReviewPull[] }) {
  return (
    <DSBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {pulls.map((pull) => (
        <PullChip key={`${pull.githubRepo}#${pull.number}`} pull={pull} />
      ))}
    </DSBox>
  );
}

function ReviewerAvatar({
  reviewer,
  users,
}: {
  reviewer: Reviewer;
  users: Author[];
}) {
  const name = userName(reviewer.userId, users);
  const snoozed =
    reviewer.snoozedUntil && new Date(reviewer.snoozedUntil) > new Date();
  return (
    <DSTooltip
      title={`${name} · ${REVIEWER_STATUS_LABEL[reviewer.status]}${
        snoozed ? " · snoozed" : ""
      }`}
    >
      <DSBox sx={{ position: "relative", display: "inline-flex" }}>
        <DSAvatar
          sx={{
            width: 28,
            height: 28,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: getAvatarColor(name),
            opacity: snoozed ? 0.5 : 1,
          }}
        >
          {getInitials(name)}
        </DSAvatar>
        <DSBox
          sx={{
            position: "absolute",
            right: -2,
            bottom: -2,
            width: 10,
            height: 10,
            borderRadius: "50%",
            border: 2,
            borderColor: "background.paper",
            bgcolor: REVIEWER_STATUS_COLOR[reviewer.status],
          }}
        />
      </DSBox>
    </DSTooltip>
  );
}

export function ReviewerAvatars({
  request,
  users,
}: {
  request: ReviewRequest;
  users: Author[];
}) {
  if (isTeamRequest(request)) {
    return <DSChip label="Anyone" size="small" sx={{ fontSize: 11 }} />;
  }
  return (
    <DSBox sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
      {request.reviewers.map((reviewer) => (
        <ReviewerAvatar
          key={reviewer.userId}
          reviewer={reviewer}
          users={users}
        />
      ))}
    </DSBox>
  );
}

export function PolicyText({ request }: { request: ReviewRequest }) {
  if (isTeamRequest(request)) {
    return (
      <DSTooltip title="Open to the team: the first approval completes it">
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          {request.status === "approved" ? "1/1" : "0/1"} · anyone
        </DSTypography>
      </DSTooltip>
    );
  }
  const approved = approvedCount(request);
  const total = request.reviewers.length;
  const text =
    request.policy === "all"
      ? `${approved}/${total} · all`
      : `${approved}/${total} · any one`;
  return (
    <DSTooltip title={POLICY_LABEL[request.policy]}>
      <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
        {text}
      </DSTypography>
    </DSTooltip>
  );
}
